import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject, takeUntil } from 'rxjs';
import { PolicyService } from 'src/app/modules/policy/policy.service';
import { IFormKey } from 'src/app/modules/shared/models/forms';
import { ICountriesObject } from 'src/app/modules/shared/models/policy.model';
import { SpinnerService } from 'src/app/services/spinner.service';
import { CommonUtils } from 'src/app/utils/CommonUtils';
import { FormUtils } from 'src/app/utils/FormUtils';
import { AthoraService } from '../../athora.service';
import {
  CODICE_NAZIONE_ITALIA,
  SIGLA_CAP_STATO_ESTERO,
  SIGLA_CITTA_STATO_ESTERO,
  SIGLA_PROVINCIA_STATO_ESTERO,
} from '../contractor-variation/contractor-variation.constants';
import {
  BIRTH_HIDDEN_FIELD_FOREIGN_STATE_SUBJECT,
  birth_subject_form_keys,
  birth_subject_form_template,
  birth_subject_form_validators,
  HIDDEN_FIELD_FOREIGN_STATE_SUBJECT,
  individual_subject_form_keys,
  individual_subject_form_template,
  individual_subject_form_validators,
  residence_subject_form_keys,
  residence_subject_form_template,
  residence_subject_form_validators,
} from './subject-pf-insert-form.constants';

@Component({
  selector: 'app-subject-pf-insert-form',
  templateUrl: './subject-pf-insert-form.component.html',
  styleUrl: './subject-pf-insert-form.component.scss',
})
export class SubjectPfInsertFormComponent implements OnInit, OnDestroy {
  individualForm: FormGroup;
  individualFormKeys: IFormKey[];
  birthForm: FormGroup;
  birthFormKeys: IFormKey[];
  residenceForm: FormGroup;
  residenceFormKeys: IFormKey[];

  private countries: ICountriesObject[];
  private destroy$ = new Subject<void>();

  private birthProvinceKey: IFormKey; // cached references for performance
  private birthPlaceKey: IFormKey;
  private residenceProvinceKey: IFormKey;
  private residencePlaceKey: IFormKey;

  constructor(
    public dialogRef: MatDialogRef<SubjectPfInsertFormComponent>,
    @Inject(MAT_DIALOG_DATA) public modalData,
    private _athoraService: AthoraService,
    private _policyService: PolicyService,
    private _spinnerService: SpinnerService
  ) {}

  ngOnInit(): void {
    this.individualFormKeys = CommonUtils.assign(individual_subject_form_keys);
    this.birthFormKeys = CommonUtils.assign(birth_subject_form_keys);
    this.residenceFormKeys = CommonUtils.assign(residence_subject_form_keys);

    this._spinnerService.show();
    this._athoraService
      .retrieveCountries()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (countries) => {
          this.countries = countries;
          this.initIndividualForm();
          this.initBirthForm();
          this.initResidenceForm();
          if (!this.modalData.readOnly) {
            this.attachCountryWatchers();
            this.attachProvinceWatchers();
          } else {
            this.preloadReadonlySelectOptions();
          }
          this._spinnerService.hide();
        },
        error: () => this._spinnerService.hide(),
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  initIndividualForm() {
    this.individualForm = FormUtils.initFormGroupFromTemplate(
      individual_subject_form_template,
      individual_subject_form_validators
    );

    const citizenshipKey = this.individualFormKeys.find(
      (key) => key.controlName === 'citizenship'
    );
    if (citizenshipKey) {
      citizenshipKey.type = 'SELECT';
      citizenshipKey.options = this.countries.map((x) => ({
        key: x.countryBDICode,
        value: x.countryDescription,
      }));
    }

    if (this.modalData.readOnly) {
      this.initReadOnlyForm(this.individualForm, this.modalData.individual);
    } else {
      const fiscalCodeControl = this.individualForm.get('taxCode');
      fiscalCodeControl.setValue(this.modalData.fiscalCode);
      fiscalCodeControl.disable();
    }
  }

  initBirthForm() {
    this.birthForm = FormUtils.initFormGroupFromTemplate(
      birth_subject_form_template,
      birth_subject_form_validators
    );

    const birthCountryKey = this.birthFormKeys.find(
      (key) => key.controlName === 'birthCountry'
    );
    if (birthCountryKey) {
      birthCountryKey.type = 'SELECT';
      birthCountryKey.options = this.countries.map((x) => ({
        key: x.countryBDICode,
        value: x.countryDescription,
      }));
    }

    // Cache keys for both readonly and editable flows
    this.birthProvinceKey = this.birthFormKeys.find(
      (key) => key.controlName === 'birthProvince'
    );
    this.birthPlaceKey = this.birthFormKeys.find(
      (key) => key.controlName === 'birthPlace'
    );

    if (this.modalData.readOnly) {
      this.initReadOnlyForm(this.birthForm, this.modalData.birth);
    } else {
      // Disable until chain satisfied (Italy country then province then place)
      this.birthForm.get('birthProvince')?.disable();
      this.birthForm.get('birthPlace')?.disable();
    }
  }

  initResidenceForm() {
    this.residenceForm = FormUtils.initFormGroupFromTemplate(
      residence_subject_form_template,
      residence_subject_form_validators
    );

    const residenceCountryKey = this.residenceFormKeys.find(
      (key) => key.controlName === 'residenceCountry'
    );
    if (residenceCountryKey) {
      residenceCountryKey.type = 'SELECT';
      residenceCountryKey.options = this.countries.map((x) => ({
        key: x.countryBDICode,
        value: x.countryDescription,
      }));
    }

    // Cache keys for both readonly and editable flows
    this.residenceProvinceKey = this.residenceFormKeys.find(
      (key) => key.controlName === 'residenceProvince'
    );
    this.residencePlaceKey = this.residenceFormKeys.find(
      (key) => key.controlName === 'residencePlace'
    );

    if (this.modalData.readOnly) {
      this.initReadOnlyForm(this.residenceForm, this.modalData.residence);
    } else {
      // Disable province and following chain until selected (Italy only)
      this.residenceForm.get('residenceProvince')?.disable();
      this.disableResidenceChainAfterProvince();
    }
  }

  private attachCountryWatchers(): void {
    const birthCountryCtrl = this.birthForm.get('birthCountry');
    const residenceCountryCtrl = this.residenceForm.get('residenceCountry');

    birthCountryCtrl?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((val) => {
        this.handleCountryChange(val, 'birth');
      });

    residenceCountryCtrl?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((val) => {
        this.handleCountryChange(val, 'residence');
      });
  }

  private attachProvinceWatchers(): void {
    const birthProvinceCtrl = this.birthForm.get('birthProvince');
    const residenceProvinceCtrl = this.residenceForm.get('residenceProvince');

    birthProvinceCtrl?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((provinceCode) => {
        this.handleProvinceChange(provinceCode, 'birth');
      });

    residenceProvinceCtrl?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((provinceCode) => {
        this.handleProvinceChange(provinceCode, 'residence');
      });
  }

  private handleProvinceChange(
    provinceCode: string,
    scope: 'birth' | 'residence'
  ): void {
    if (!provinceCode) {
      // Clear places if province cleared
      if (scope === 'birth' && this.birthPlaceKey) {
        this.birthPlaceKey.options = [];
        this.birthForm.patchValue({ birthPlace: null });
        this.birthForm.get('birthPlace')?.disable();
      }
      if (scope === 'residence' && this.residencePlaceKey) {
        this.residencePlaceKey.options = [];
        this.residenceForm.patchValue({ residencePlace: null });
        this.residenceForm.get('residencePlace')?.disable();
        this.disableResidenceChainAfterProvince();
      }
      return;
    }

    this._spinnerService.show();
    this._policyService
      .retrieveCities(provinceCode)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const mapped = (response || []).map((cityObj: any) => ({
            key: cityObj.cityName,
            value: cityObj.cityName,
          }));
          if (scope === 'birth' && this.birthPlaceKey) {
            this.birthPlaceKey.type = 'SELECT';
            this.birthPlaceKey.options = mapped;
            this.birthForm.get('birthPlace')?.reset();
            this.birthForm.get('birthPlace')?.enable();
          }
          if (scope === 'residence' && this.residencePlaceKey) {
            this.residencePlaceKey.type = 'SELECT';
            this.residencePlaceKey.options = mapped;
            this.residenceForm.get('residencePlace')?.reset();
            this.residenceForm.get('residencePlace')?.enable();
            // Enable remaining residence chain controls now province chosen
            [
              'residenceToponym',
              'residenceAddress',
              'residenceAddressNumber',
              'residencePostalCode',
            ].forEach((c) => this.residenceForm.get(c)?.enable());
          }
        },
        complete: () => this._spinnerService.hide(),
        error: () => this._spinnerService.hide(),
      });
  }

  private handleCountryChange(
    selected: string,
    scope: 'birth' | 'residence'
  ): void {
    if (!selected) return;
    const isItaly = selected === CODICE_NAZIONE_ITALIA;

    if (isItaly) {
      this._spinnerService.show();
      this._athoraService
        .retrieveProvinces()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (provinces) => {
            const mapped = (provinces || []).map((p: any) => ({
              key: p.provinceCode,
              value: p.provinceDescription,
            }));
            if (scope === 'birth' && this.birthProvinceKey) {
              this.birthProvinceKey.type = 'SELECT';
              this.birthProvinceKey.options = mapped;
              this.birthForm.get('birthProvince')?.reset();
              this.birthForm.get('birthProvince')?.enable();
              // Keep place disabled until province chosen
              this.birthForm.get('birthPlace')?.disable();
            }
            if (scope === 'residence' && this.residenceProvinceKey) {
              this.residenceProvinceKey.type = 'SELECT';
              this.residenceProvinceKey.options = mapped;
              this.residenceForm.get('residenceProvince')?.reset();
              this.residenceForm.get('residenceProvince')?.enable();
              this.disableResidenceChainAfterProvince();
            }
          },
          complete: () => this._spinnerService.hide(),
          error: () => this._spinnerService.hide(),
        });
    } else {
      scope === 'birth'
        ? this.manageBirthForeignState()
        : this.manageResidenceForeignState();
    }
  }

  private manageBirthForeignState(): void {
    this.birthFormKeys.forEach((key) => {
      if (BIRTH_HIDDEN_FIELD_FOREIGN_STATE_SUBJECT.includes(key.controlName)) {
        const control = this.birthForm.get(key.controlName);
        control.reset();
        control.disable();
        control.setValidators(null);
        if (key.controlName === 'birthPlace') {
          key.type = 'INPUT';
          control.patchValue(SIGLA_CITTA_STATO_ESTERO, { emitEvent: false });
        } else if (key.controlName === 'birthProvince') {
          key.type = 'INPUT';
          control.patchValue(SIGLA_PROVINCIA_STATO_ESTERO, {
            emitEvent: false,
          });
        }
      }
    });
  }

  manageResidenceForeignState(): void {
    this.residenceFormKeys.forEach((key) => {
      if (HIDDEN_FIELD_FOREIGN_STATE_SUBJECT.includes(key.controlName)) {
        const control = this.residenceForm.get(key.controlName);
        control.reset();
        control.disable();
        control.setValidators(null);
        if (key.controlName === 'residencePlace') {
          key.type = 'INPUT';
          control.patchValue(SIGLA_CITTA_STATO_ESTERO, { emitEvent: false });
        } else if (key.controlName === 'residenceProvince') {
          key.type = 'INPUT';
          control.patchValue(SIGLA_PROVINCIA_STATO_ESTERO, {
            emitEvent: false,
          });
        } else if (key.controlName === 'residencePostalCode') {
          control.patchValue(SIGLA_CAP_STATO_ESTERO, { emitEvent: false });
        }
      } else if (key.controlName !== 'residenceCountry') {
        this.residenceForm.get(key.controlName).reset();
        this.residenceForm.get(key.controlName).enable();
        if (key.controlName === 'residenceToponym') {
          this.residenceForm.get('residenceToponym').setValidators(null);
        }
      }
    });
  }

  private disableResidenceChainAfterProvince(): void {
    [
      'residencePlace',
      'residenceToponym',
      'residenceAddress',
      'residenceAddressNumber',
      'residencePostalCode',
    ].forEach((c) => {
      const ctrl = this.residenceForm.get(c);
      if (ctrl) {
        ctrl.reset();
        ctrl.disable();
      }
    });
  }

  // Populate select options in readonly mode so disabled selects still display proper labels
  private preloadReadonlySelectOptions(): void {
    const birthCountry = this.birthForm.get('birthCountry')?.value;
    const birthProvince = this.birthForm.get('birthProvince')?.value;
    const residenceCountry = this.residenceForm.get('residenceCountry')?.value;
    const residenceProvince = this.residenceForm.get('residenceProvince')?.value;

    // Birth: If Italy, load provinces and cities; otherwise convert to INPUT for foreign state
    if (birthCountry === CODICE_NAZIONE_ITALIA && this.birthProvinceKey) {
      this._athoraService
        .retrieveProvinces()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (provinces) => {
            this.birthProvinceKey.type = 'SELECT';
            this.birthProvinceKey.options = (provinces || []).map((p: any) => ({
              key: p.provinceCode,
              value: p.provinceDescription,
            }));
          },
        });

      if (birthProvince && this.birthPlaceKey) {
        this._policyService
          .retrieveCities(birthProvince)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (cities) => {
              this.birthPlaceKey.type = 'SELECT';
              this.birthPlaceKey.options = (cities || []).map((c: any) => ({
                key: c.cityName,
                value: c.cityName,
              }));
            },
          });
      }
    } else if (birthCountry && birthCountry !== CODICE_NAZIONE_ITALIA) {
      // Foreign country: convert province and place to INPUT type for readonly display
      if (this.birthProvinceKey) {
        this.birthProvinceKey.type = 'INPUT';
      }
      if (this.birthPlaceKey) {
        this.birthPlaceKey.type = 'INPUT';
      }
    }

    // Residence: If Italy, load provinces and cities; otherwise convert to INPUT for foreign state
    if (residenceCountry === CODICE_NAZIONE_ITALIA && this.residenceProvinceKey) {
      this._athoraService
        .retrieveProvinces()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (provinces) => {
            this.residenceProvinceKey.type = 'SELECT';
            this.residenceProvinceKey.options = (provinces || []).map((p: any) => ({
              key: p.provinceCode,
              value: p.provinceDescription,
            }));
          },
        });

      if (residenceProvince && this.residencePlaceKey) {
        this._policyService
          .retrieveCities(residenceProvince)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (cities) => {
              this.residencePlaceKey.type = 'SELECT';
              this.residencePlaceKey.options = (cities || []).map((c: any) => ({
                key: c.cityName,
                value: c.cityName,
              }));
            },
          });
      }
    } else if (residenceCountry && residenceCountry !== CODICE_NAZIONE_ITALIA) {
      // Foreign country: convert province and place to INPUT type for readonly display
      if (this.residenceProvinceKey) {
        this.residenceProvinceKey.type = 'INPUT';
      }
      if (this.residencePlaceKey) {
        this.residencePlaceKey.type = 'INPUT';
      }
    }
  }

  private initReadOnlyForm(
    form: FormGroup,
    modalDataForm: Record<string, any>
  ) {
    form.patchValue(modalDataForm);
    Object.values(form.controls).forEach((control) => control.disable());
  }

  get isConfirmButtonDisabled(): boolean {
    return (
      !this.individualForm?.valid ||
      !this.birthForm?.valid ||
      !this.residenceForm?.valid
    );
  }

  addNewSubject() {
    this.dialogRef.close({
      individual: this.individualForm.getRawValue(),
      birth: this.birthForm.getRawValue(),
      residence: this.residenceForm.getRawValue(),
      readOnly: true,
    });
  }

  close() {
    this.dialogRef.close({ resetInput: true });
  }
}
